import React, { useState } from "react";
import { Card, CardBody, CardHeader, Divider } from "@heroui/react";
import { HeartIcon, ChatBubbleLeftIcon, EyeIcon, PlayIcon } from "@heroicons/react/24/outline";
import CenteredHeroNav from "../components/CenteredHeroNav";

// Theme Option 1: Electric Blue (Current-ish)
const electricBlue = {
  name: "Electric Blue",
  description: "Modern, energetic, tech-forward",
  css: {
    "--color-primary": "#3b82f6",
    "--color-primary-hover": "#2563eb",
    "--color-secondary": "#8b5cf6",
    "--color-accent": "#06b6d4",
    "--color-surface": "#1e293b",
    "--color-surface-light": "#334155",
    "--color-text": "#f1f5f9",
    "--color-text-secondary": "#94a3b8",
    background: "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)",
  }
};

// Theme Option 2: Cyberpunk Neon
const cyberpunkNeon = {
  name: "Cyberpunk Neon",
  description: "Vibrant, futuristic, high-energy",
  css: {
    "--color-primary": "#ff0080",
    "--color-primary-hover": "#d6006b",
    "--color-secondary": "#00ff9f",
    "--color-accent": "#ffff00",
    "--color-surface": "#0a0a0f",
    "--color-surface-light": "#1a1a2e",
    "--color-text": "#ffffff",
    "--color-text-secondary": "#b4b4d4",
    background: "linear-gradient(135deg, #0a0a0f 0%, #1a1a2e 50%, #16213e 100%)",
  }
};

// Theme Option 3: Emerald Dream
const emeraldDream = {
  name: "Emerald Dream",
  description: "Natural, eco-friendly, sustainable",
  css: {
    "--color-primary": "#10b981",
    "--color-primary-hover": "#059669",
    "--color-secondary": "#14b8a6",
    "--color-accent": "#84cc16",
    "--color-surface": "#064e3b",
    "--color-surface-light": "#065f46",
    "--color-text": "#ecfdf5",
    "--color-text-secondary": "#a7f3d0",
    background: "linear-gradient(135deg, #022c22 0%, #064e3b 50%, #065f46 100%)",
  }
};

// Theme Option 4: Sunset Orange
const sunsetOrange = {
  name: "Sunset Orange",
  description: "Warm, inviting, energetic",
  css: {
    "--color-primary": "#f97316",
    "--color-primary-hover": "#ea580c",
    "--color-secondary": "#fb923c",
    "--color-accent": "#fbbf24",
    "--color-surface": "#7c2d12",
    "--color-surface-light": "#9a3412",
    "--color-text": "#fff7ed",
    "--color-text-secondary": "#fed7aa",
    background: "linear-gradient(135deg, #431407 0%, #7c2d12 50%, #9a3412 100%)",
  }
};

// Theme Option 5: Coastal Breeze (Your Custom Palette)
const coastalBreeze = {
  name: "Coastal Breeze",
  description: "Sophisticated, earthy, balanced",
  css: {
    "--color-primary": "#2a9d8f", // persian_green
    "--color-primary-hover": "#217e73", // persian_green 400
    "--color-secondary": "#e9c46a", // saffron
    "--color-accent": "#f4a261", // sandy_brown
    "--color-surface": "#264653", // charcoal
    "--color-surface-light": "#3f7489", // charcoal 600
    "--color-text": "#cadee7", // charcoal 900
    "--color-text-secondary": "#95bece", // charcoal 800
    background: "linear-gradient(135deg, #172b32 0%, #264653 50%, #3f7489 100%)",
  }
};

const themes = [electricBlue, cyberpunkNeon, emeraldDream, sunsetOrange, coastalBreeze];

const ThemePreview = () => {
  const [selectedTheme, setSelectedTheme] = useState(null);

  const ThemeCard = ({ theme, index }) => (
    <div
      className="relative rounded-2xl overflow-hidden shadow-2xl transition-all duration-300 hover:scale-105"
      style={theme.css}
    >
      {/* Background */}
      <div className="absolute inset-0" style={{ background: theme.css.background }} />

      {/* Content */}
      <div className="relative z-10 p-6 space-y-4">
        {/* Theme Header */}
        <div className="text-center space-y-2 pb-4">
          <h2 className="text-2xl font-bold" style={{ color: theme.css["--color-text"] }}>
            {theme.name}
          </h2>
          <p className="text-sm" style={{ color: theme.css["--color-text-secondary"] }}>
            {theme.description}
          </p>
        </div>

        {/* Mini Navigation Bar */}
        <div
          className="rounded-full px-4 py-2 backdrop-blur-xl backdrop-saturate-150 flex justify-between items-center"
          style={{ backgroundColor: `${theme.css["--color-surface-light"]}80` }}
        >
          <span className="font-bold text-sm" style={{ color: theme.css["--color-text"] }}>
            EV Eye Witness
          </span>
          <div className="flex gap-2">
            <button
              className="px-3 py-1 rounded-full text-xs font-medium transition-all"
              style={{
                backgroundColor: theme.css["--color-primary"],
                color: "#ffffff",
              }}
            >
              Login
            </button>
          </div>
        </div>

        {/* Video Card Preview */}
        <div
          className="rounded-xl overflow-hidden backdrop-blur-md"
          style={{ backgroundColor: `${theme.css["--color-surface-light"]}90` }}
        >
          {/* Fake Video Thumbnail */}
          <div
            className="relative h-32 flex items-center justify-center"
            style={{ backgroundColor: theme.css["--color-surface"] }}
          >
            <PlayIcon
              className="w-12 h-12 opacity-60"
              style={{ color: theme.css["--color-text"] }}
            />
          </div>

          {/* Video Info */}
          <div className="p-4 space-y-3">
            <h3 className="font-semibold text-sm" style={{ color: theme.css["--color-text"] }}>
              Tesla Model 3 - Highway Footage
            </h3>

            <div className="flex flex-wrap gap-2">
              <span
                className="px-2 py-1 rounded-full text-xs flex items-center gap-1"
                style={{
                  backgroundColor: `${theme.css["--color-primary"]}20`,
                  color: theme.css["--color-primary"]
                }}
              >
                <EyeIcon className="w-3 h-3" />
                1.2K views
              </span>
              <span
                className="px-2 py-1 rounded-full text-xs"
                style={{
                  backgroundColor: `${theme.css["--color-secondary"]}20`,
                  color: theme.css["--color-secondary"]
                }}
              >
                California
              </span>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 pt-2">
              <button
                className="flex-1 py-2 rounded-lg text-xs font-medium transition-all hover:opacity-80"
                style={{
                  backgroundColor: theme.css["--color-primary"],
                  color: "#ffffff",
                }}
              >
                <span className="flex items-center justify-center gap-1">
                  <HeartIcon className="w-4 h-4" />
                  Like
                </span>
              </button>
              <button
                className="flex-1 py-2 rounded-lg text-xs font-medium transition-all hover:opacity-80"
                style={{
                  backgroundColor: `${theme.css["--color-surface"]}`,
                  color: theme.css["--color-text"],
                  border: `1px solid ${theme.css["--color-primary"]}40`
                }}
              >
                <span className="flex items-center justify-center gap-1">
                  <ChatBubbleLeftIcon className="w-4 h-4" />
                  Comment
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Select Button */}
        <button
          onClick={() => setSelectedTheme(theme)}
          className="w-full py-3 rounded-xl font-bold text-sm transition-all hover:scale-105 shadow-lg"
          style={{
            backgroundColor: theme.css["--color-accent"],
            color: theme.css["--color-surface"],
          }}
        >
          {selectedTheme?.name === theme.name ? "✓ Selected" : `Choose ${theme.name}`}
        </button>

        {/* Color Swatches */}
        <div className="flex justify-center gap-2 pt-2">
          <div
            className="w-8 h-8 rounded-full border-2 border-white/30"
            style={{ backgroundColor: theme.css["--color-primary"] }}
            title="Primary"
          />
          <div
            className="w-8 h-8 rounded-full border-2 border-white/30"
            style={{ backgroundColor: theme.css["--color-secondary"] }}
            title="Secondary"
          />
          <div
            className="w-8 h-8 rounded-full border-2 border-white/30"
            style={{ backgroundColor: theme.css["--color-accent"] }}
            title="Accent"
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen theme-gradient-surface">
      <CenteredHeroNav />

      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-12 space-y-4">
          <h1 className="text-4xl font-bold">Choose Your Theme</h1>
          <p className="text-lg text-default-500">
            Select a color palette that matches your style
          </p>
          {selectedTheme && (
            <Card className="max-w-md mx-auto">
              <CardBody className="text-center">
                <p className="text-sm">
                  You selected: <strong>{selectedTheme.name}</strong>
                </p>
                <p className="text-xs text-default-500 mt-1">
                  This is just a preview. Implementation requires code changes.
                </p>
              </CardBody>
            </Card>
          )}
        </div>

        {/* Theme Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-8">
          {themes.map((theme, index) => (
            <ThemeCard key={theme.name} theme={theme} index={index} />
          ))}
        </div>

        {/* Instructions */}
        <Card className="mt-12 max-w-3xl mx-auto">
          <CardHeader>
            <h3 className="text-xl font-bold">About These Themes</h3>
          </CardHeader>
          <Divider />
          <CardBody className="space-y-4">
            <div>
              <h4 className="font-semibold mb-2">1. Electric Blue (Current)</h4>
              <p className="text-sm text-default-600">
                Clean, professional, tech-forward design. Great for a modern EV platform.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-2">2. Cyberpunk Neon</h4>
              <p className="text-sm text-default-600">
                Bold, vibrant, futuristic. High contrast with neon accents for maximum impact.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-2">3. Emerald Dream</h4>
              <p className="text-sm text-default-600">
                Natural, eco-friendly aesthetic. Perfect for emphasizing EV sustainability.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-2">4. Sunset Orange</h4>
              <p className="text-sm text-default-600">
                Warm, energetic, inviting. Creates a friendly, approachable atmosphere.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-2">5. Coastal Breeze ⭐ NEW</h4>
              <p className="text-sm text-default-600">
                Sophisticated earthy tones with teal and saffron accents. Perfect blend of professional and welcoming, inspired by coastal landscapes. Charcoal base with Persian green primary.
              </p>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default ThemePreview;
